import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Button,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Security,
  Storage,
  Share,
  Delete,
  ContactMail,
  School,
  Groups,
  Assignment,
  Analytics
} from '@mui/icons-material';
import SEO from '../utils/SEO';

/**
 * Privacy Policy Component
 * 
 * Comprehensive GDPR and Google OAuth compliant privacy policy for TeachAssist.
 * Covers all data collection, usage, storage, and user rights.
 */
const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Level Up Teaching privacy policy - how we collect, use, and protect your data. Learn about our GDPR-compliant practices and your rights."
        canonicalUrl="https://levelupteaching.ai/privacy-policy"
        keywords="privacy policy, data protection, GDPR, educational technology privacy, student data privacy"
      />
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom color="primary">
            Privacy Policy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Effective Date: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to Level Up Teaching ("we," "our," or "us"). This Privacy Policy explains how we collect, 
            use, disclose, and safeguard your information when you use our educational platform service 
            located at https://levelupteaching.ai (the "Service").
          </Typography>
          <Typography variant="body1" paragraph>
            We are committed to protecting your privacy and ensuring transparency about our data practices. 
            This policy applies to all users of our Service, including teachers, educators, and school 
            administrators.
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mt: 3, 
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.200'
            }}
          >
            <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
              Google Classroom Data Usage
            </Typography>
            <Typography variant="body1" paragraph>
              When you connect your Google Classroom account to our Service, we access Google Classroom 
              data solely to enable core educational features. Specifically:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• We access student rosters to help you organize reading groups and create differentiated lesson plans" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• We access course information to help you manage your classes within our platform" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• This data is used exclusively for educational purposes and is never sold or shared with third parties" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• You can revoke our access at any time through your Google Account settings" />
              </ListItem>
            </List>
          </Paper>
        </Box>

        {/* Information We Collect */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            2. Information We Collect
          </Typography>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
            2.1 Information You Provide
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <ContactMail color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Account Information"
                secondary="Name, email address, profile picture, and authentication credentials when you create an account or sign in via Google or LinkedIn"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <School color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Professional Information"
                secondary="School name, grade levels taught, subjects taught, years of experience, and professional biography"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Groups color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Student Information"
                secondary="Student names, reading levels, group assignments, and academic progress data (no personally identifiable student information is stored without explicit consent)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Assignment color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Educational Content"
                secondary="Lesson plans, assessments, student work samples, and instructional materials you create or upload"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
            2.2 Information from Google Services
          </Typography>
          <Typography variant="body1" paragraph>
            When you connect your Google account, we may access:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="• Google Classroom data (courses, rosters, assignments) - only with your explicit permission"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Google Drive files - only files you explicitly share or import into our Service"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="• Basic profile information (name, email, profile picture)"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
            2.3 Automatically Collected Information
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Analytics color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Usage Information"
                secondary="How you interact with our Service, features used, time spent, and navigation patterns"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Security color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Technical Information"
                secondary="IP address, browser type, device information, and access times for security and performance purposes"
              />
            </ListItem>
          </List>
        </Box>

        {/* How We Use Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use the information we collect for the following purposes:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="• Provide and maintain our educational Service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Generate AI-powered lesson plans and educational content" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Manage student groups and track academic progress" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Authenticate your account and ensure security" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Improve our Service and develop new features" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Provide customer support and respond to inquiries" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Send important updates about our Service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Comply with legal obligations and protect our rights" />
            </ListItem>
          </List>
        </Box>

        {/* Information Sharing */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            4. Information Sharing and Disclosure
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, trade, or rent your personal information to third parties. We may share 
            your information only in the following circumstances:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Security color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Service Providers"
                secondary="Trusted third-party services that help us operate our platform (OpenAI for AI services, MongoDB for data storage, Vercel for hosting)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Share color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Legal Requirements"
                secondary="When required by law, court order, or to protect our rights and safety"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Groups color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="School Districts"
                secondary="With your school or district administrators when required for educational compliance or when you explicitly authorize such sharing"
              />
            </ListItem>
          </List>
        </Box>

        {/* Data Storage and Security */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            5. Data Storage and Security
          </Typography>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
            5.1 How We Store Your Information
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Storage color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Secure Cloud Storage"
                secondary="Data is stored on secure cloud servers with MongoDB Atlas and Vercel, with encryption at rest and in transit"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Security color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Data Protection"
                secondary="We implement industry-standard security measures including encryption, secure authentication, and regular security audits"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
            5.2 Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your information for as long as your account is active or as needed to provide 
            you services. You may request deletion of your account and data at any time through your 
            account settings or by contacting us.
          </Typography>
        </Box>

        {/* Your Rights */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            6. Your Rights and Choices
          </Typography>
          <Typography variant="body1" paragraph>
            You have the following rights regarding your personal information:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Access"
                secondary="Request access to the personal information we hold about you"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Correction"
                secondary="Update or correct your personal information through your account settings"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Deletion"
                secondary="Request deletion of your account and personal data"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Portability"
                secondary="Request export of your data in a commonly used format"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Opt-out"
                secondary="Unsubscribe from non-essential communications"
              />
            </ListItem>
          </List>

          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 3 }}>
            6.1 Revoking Google Access
          </Typography>
          <Typography variant="body1" paragraph>
            You can revoke our access to your Google account data at any time by visiting your{' '}
            <Link 
              href="https://myaccount.google.com/permissions" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Google Account Permissions page
            </Link>{' '}
            and removing TeachAssist from your authorized applications.
          </Typography>
        </Box>

        {/* Children's Privacy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            7. Children's Privacy (COPPA Compliance)
          </Typography>
          <Typography variant="body1" paragraph>
            Our Service is designed for use by teachers and educators, not directly by children under 13. 
            We do not knowingly collect personal information from children under 13 without verifiable 
            parental consent or direct authorization from the educational institution.
          </Typography>
          <Typography variant="body1" paragraph>
            When student information is collected through teacher use of our Service, it is done under 
            the educational exception provisions of COPPA and with appropriate school authorization.
          </Typography>
        </Box>

        {/* International Users */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            8. International Users
          </Typography>
          <Typography variant="body1" paragraph>
            If you are accessing our Service from outside the United States, please be aware that your 
            information may be transferred to, stored, and processed in the United States where our 
            servers are located. By using our Service, you consent to this transfer.
          </Typography>
        </Box>

        {/* Changes to Policy */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            9. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the "Last updated" date. Continued 
            use of our Service after changes constitutes acceptance of the updated policy.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            10. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body1">
              <strong>Email:</strong> privacy@levelupteaching.ai
            </Typography>
            <Typography variant="body1">
              <strong>Website:</strong> https://levelupteaching.ai
            </Typography>
            <Typography variant="body1">
              <strong>Address:</strong> Level Up Teaching, LLC
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Footer Actions */}
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={handleBackClick}>
            Go Back
          </Button>
          <Button 
            variant="contained" 
            href="mailto:privacy@levelupteaching.ai"
          >
            Contact Us
          </Button>
        </Stack>
      </Paper>
    </Container>
    </>
  );
};

export default PrivacyPolicy;
